import type { EnrichedRoom } from '../types/rooms'
import type { WakeupCall } from '../types/wakeup'
import { extensionUuidKey } from './extensionUuid'
import { computeDisplayStatus } from './wakeupEnrichment'

export type WakeupNotificationTone = 'tried' | 'cleared' | null
type RoomWakeupMatchTarget = Pick<
  EnrichedRoom,
  'uuid' | 'extension_uuid' | 'number' | 'name'
>

const normalizeRoomValue = (value: string | null | undefined) => {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return ''
  return trimmed.replace(/^0+(\d)/, '$1').toLowerCase()
}

const matchesRoom = (wakeup: WakeupCall, room: RoomWakeupMatchTarget) => {
  const roomKey = extensionUuidKey(room.extension_uuid)
  if (roomKey && extensionUuidKey(wakeup.extension_uuid) === roomKey) {
    return true
  }

  const wakeupRoomNumber = normalizeRoomValue(wakeup.room_number)
  const roomNumber = normalizeRoomValue(room.number)
  const roomName = normalizeRoomValue(room.name)

  if (!wakeupRoomNumber) return false
  return wakeupRoomNumber === roomNumber || wakeupRoomNumber === roomName
}

const isDashboardWakeupStatus = (wc: WakeupCall) => {
  const status = wc.display_status ?? computeDisplayStatus(wc)
  return status === 'scheduled' || status === 'tried' || status === 'cleared'
}

/**
 * Build wake-up rows relevant to a room for dashboard displays.
 * Legacy dashboard excludes canceled (deleted) rows.
 */
export const getRoomDashboardWakeups = (
  results: WakeupCall[],
  room: RoomWakeupMatchTarget,
) => {
  return results
    .filter((wc) => matchesRoom(wc, room) && isDashboardWakeupStatus(wc))
    .sort((a, b) =>
      (a.wuc_timestamp ?? '').localeCompare(b.wuc_timestamp ?? ''),
    )
}

/**
 * Legacy color behavior:
 * - orange when any scheduled or tried wake-up exists
 * - blue when only cleared wake-ups exist
 * - gray when none
 */
export const getWakeupNotificationTone = (
  wakeups: WakeupCall[],
): WakeupNotificationTone => {
  if (
    wakeups.some((wc) => {
      const status = wc.display_status ?? computeDisplayStatus(wc)
      return status === 'scheduled' || status === 'tried'
    })
  ) {
    return 'tried'
  }
  if (
    wakeups.some((wc) => {
      const status = wc.display_status ?? computeDisplayStatus(wc)
      return status === 'cleared'
    })
  ) {
    return 'cleared'
  }
  return null
}
